import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  Box,
  Button,
  DropdownV5,
  Flex,
  MuiModal,
  Text,
  Tooltip,
} from '@pinchenterprisesnpm/friday-ui'
import { useModal } from '@src/hooks/useModal'
import { faPen } from '@fortawesome/pro-regular-svg-icons'
import { TimePicker } from '@src/components/business/TimePicker'
import { getBreakValue } from '@src/helpers/shiftHelpers'
import { TDay, TSingleDayShift } from '@src/helpers/shiftHelpers/types'
import { connect, FormikProps } from 'formik'
import { IScheduleSectionFormValues } from '@src/features/shift-post-v2/steps/shift-details-step/components/schedule-section/schedule-section.types'
import { hasBusinessPayRateUpdateRestriction } from '@src/helpers/businessEntityHelpers'
import { PAY_RATE_UPDATE_RESTRICTION_TOOLTIP } from '@src/config/TooltipContent/TooltipContent.js'
import { getPayRateError } from '@src/features/shift-post-v2/steps/shift-details-step/components/schedule-section/schedule-section.utils'
import { PayRateFeedback } from '@src/features/shift-post-v2/modals/new-edit-modal/new-edit-modal/components/pay-rate-feedback'
import { setNumberTo2DecimalsOnly } from '@src/helpers/numberHelpers'
import { ExtendedInput } from './edit-shit-modal.style'
import {
  handleHourChange,
  handleBreakChange,
  handlePayRateChange,
} from './form-handlers.ts'

const { Header, Content } = MuiModal

interface IEditShiftModalProps {
  day?: TSingleDayShift | TDay
  singleDayDate?: Date
  formik?: FormikProps<IScheduleSectionFormValues>
  customIsOpen?: boolean
  customCloseModal?: () => void
  hasCustomToggleButton?: boolean
  buttonSize?: 'default' | 'xsmall' | 'small' | 'large' | 'xlarge' | undefined
  directlyEditPayRate?: boolean
}

export const EditShiftModal = connect<IEditShiftModalProps>(
  ({
    day,
    formik,
    singleDayDate,
    customIsOpen,
    customCloseModal,
    hasCustomToggleButton,
    buttonSize = 'default',
    directlyEditPayRate,
  }) => {
    const { values, setFieldValue } = formik
    const suggestedPayRate = values.suggested_pay_rate || 0

    const payValue = day ? day?.pay_value : values.pay_value
    const breakValue = day?.break === undefined ? values.break : day?.break
    const hourStart = day?.hour_start || values.hour_start
    const hourEnd = day?.hour_end || values.hour_end

    const { handleBlur } = formik

    const [isOpen, toggleModal, closeModal] = useModal()

    const finalIsOpen = customIsOpen ?? isOpen
    const finalCloseModal = customCloseModal ?? closeModal

    const payRateError = getPayRateError(formik, Number(payValue))

    const onStartTimeChange = (value: string) => {
      if (singleDayDate) {
        setFieldValue('hour_start', value)
        return
      }

      if (day) {
        handleHourChange(value, formik, day, true)
      }
    }

    const onEndTimeChange = (value: string) => {
      if (singleDayDate) {
        setFieldValue('hour_end', value)
        return
      }

      if (day) {
        handleHourChange(value, formik, day, false)
      }
    }

    const onPayRateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      // Limit to 2 decimal places
      const trimmedValue = setNumberTo2DecimalsOnly(e.target.value)
      if (singleDayDate) {
        setFieldValue('pay_value', trimmedValue)
        return
      }

      if (day) {
        // Create a synthetic event with the trimmed value
        const syntheticEvent = {
          ...e,
          target: {
            ...e.target,
            value: trimmedValue,
          },
        }
        handlePayRateChange(
          syntheticEvent as React.ChangeEvent<HTMLInputElement>,
          formik,
          day,
        )

        directlyEditPayRate && setFieldValue('pay_value', trimmedValue)
      }
    }

    const onBreakChange = (selected: any) => {
      if (singleDayDate) {
        setFieldValue('break', selected.value)
        return
      }

      if (day) {
        handleBreakChange(selected, formik, day)
      }
    }

    return (
      <>
        {!hasCustomToggleButton && (
          <Button type="warning" size={buttonSize} onClick={toggleModal}>
            <FontAwesomeIcon icon={faPen} />
          </Button>
        )}

        <MuiModal
          isOpen={finalIsOpen}
          closeModal={finalCloseModal}
          maxWidth="xs"
          fullWidth
        >
          <Header title="Edit Shift" />

          <Content>
            <Flex>
              <Box mr="md" width="50%">
                <Text mb="xs" fontWeight="normal">
                  Start Time
                </Text>

                <TimePicker
                  value={hourStart}
                  animated
                  width="auto"
                  height="auto"
                  right="auto"
                  left="auto"
                  onChange={value => onStartTimeChange(value)}
                />
              </Box>

              <Box width="50%">
                <Text mb="xs" fontWeight="normal">
                  End Time
                </Text>

                <TimePicker
                  value={hourEnd}
                  animated
                  width="auto"
                  height="auto"
                  right="auto"
                  left="auto"
                  onChange={value => onEndTimeChange(value)}
                />
              </Box>
            </Flex>

            <Flex mt="md">
              <Box mr="md" width="50%">
                <Text mb="xs" fontWeight="normal">
                  Hourly Pay Rate
                </Text>

                <Flex>
                  <Tooltip
                    isVisible={hasBusinessPayRateUpdateRestriction()}
                    text={PAY_RATE_UPDATE_RESTRICTION_TOOLTIP}
                  >
                    <span>
                      <ExtendedInput
                        placeholder="Pay rate"
                        type="number"
                        name="pay_value"
                        id="pay_value"
                        value={payValue}
                        step="any"
                        isDisabled={hasBusinessPayRateUpdateRestriction()}
                        onChange={onPayRateChange}
                        onBlur={handleBlur}
                        errorMessage={payRateError}
                        onKeyDown={e => {
                          if (['e', 'E', '-', '+'].includes(e.key)) {
                            e.preventDefault()
                          }
                        }}
                      />
                    </span>
                  </Tooltip>
                </Flex>
                <PayRateFeedback
                  suggestedPayRate={Number(suggestedPayRate)}
                  payValue={Number(payValue)}
                />
              </Box>

              <Box width="50%">
                <Text mb="xs" fontWeight="normal">
                  Unpaid break
                </Text>

                <DropdownV5
                  isOutlined
                  size="large"
                  options={getBreakValue()}
                  width="170px"
                  maxMenuHeight={100}
                  value={
                    getBreakValue().find(
                      opt => opt.value === (breakValue || 0),
                    ) || getBreakValue()[0]
                  }
                  onChange={onBreakChange}
                />
              </Box>
            </Flex>
          </Content>
        </MuiModal>
      </>
    )
  },
)
